import React, { createContext, useCallback, useEffect, useMemo } from 'react';
import useSWRImmutable from 'swr/immutable';
import { Configuracao, Context, Contexts, Props, Provider, SourceResult } from './config.types';
import { consultarConfiguracoesMarcaService } from './internal/marcas-configs/service/consultar-configuracoes-marca.service';
import { consultarTextosPorMarcaService } from './internal/marcas-textos/service/consultar-textos-por-marca.service';
import { ModeloApresentacaoProvider } from './internal/modelo-apresentacao/modelo-apresentacao.provider';
import { TogglesProvider } from './internal/toggles/toggles.provider';

export const ConfigContext = createContext<Configuracao | null>(null);

export const ConfigProvider: Provider = ({ source, onLoading, onError, children }: Props) => {
  const config: SourceResult = useMemo(() => {
    const { contexts, brand, domain, entries } = source();
    const map = new Map<string, unknown>(Object.entries(entries));
    return { contexts, brand, domain, entries: map };
  }, [source]);

  const contexts: Contexts = useMemo((): Contexts => {
    const key = `${window.location.protocol}//${window.location.host}`;
    const current: Context = config.contexts.find((ctx) => {
      console.log(ctx.host, key);
      return ctx.host === key;
    });
    if (current) {
      return {
        current,
        availables: config.contexts,
      };
    }
    throw new Error('Contexto de aplicação não encontrado');
  }, [config]);

  const addValue = useCallback(
    function <T>(key: string, value: T): void {
      config.entries.set(key, value);
    },
    [config],
  );

  const getValue = useCallback(
    function <T>(key: string): T | null {
      const value = config.entries.get(key);
      if (value === null || value === undefined) {
        return null;
      }
      return value as T;
    },
    [config],
  );

  const getValueOrDefault = useCallback(
    function <T>(key: string, defaultValue: T): T {
      const value = getValue<T>(key);
      if (value === null) {
        return defaultValue;
      }
      return value;
    },
    [getValue],
  );

  const getValueOrThrow = useCallback(
    function <T>(key: string): T {
      const value = getValue<T>(key);
      if (value === null) {
        throw new Error(`A configuração "${key}" não existe`);
      }
      return value;
    },
    [getValue],
  );

  const urlBase = getValueOrThrow<string>('API_DEDICADA');

  /**
   * Fetch textos de marca
   */
  const {
    data: textos,
    isLoading: loadingTextos,
    error: errorTextos,
  } = useSWRImmutable(['textos-marca'], () =>
    consultarTextosPorMarcaService({
      domain: config.domain,
      brand: config.brand,
      urlBase,
    }),
  );

  useEffect(() => {
    if (loadingTextos || errorTextos) {
      return;
    }
    Object.entries(textos).forEach(([key, value]) => {
      addValue(key, value);
    });
  }, [addValue, errorTextos, loadingTextos, textos]);

  /**
   * Fetch configs de marca
   */
  const {
    data: configsMarca,
    isLoading: loadingConfigsMarca,
    error: errorConfigsMarca,
  } = useSWRImmutable(['configs-marca'], () =>
    consultarConfiguracoesMarcaService({
      domain: config.domain,
      brand: config.brand,
      urlBase,
    }),
  );

  // Single Responsability Principle -> um useEffect dedicado pro addData
  useEffect(() => {
    if (loadingConfigsMarca || errorConfigsMarca) {
      return;
    }
    Object.entries(configsMarca).forEach(([key, value]) => {
      addValue(key, value);
    });
  }, [addValue, configsMarca, errorConfigsMarca, loadingConfigsMarca]);

  const result = useMemo<Configuracao | null>(() => {
    if (config && contexts) {
      return {
        brand: config.brand,
        appContext: contexts.current.host,
        contexts,
        domain: config.domain,
        addValue,
        getValue,
        getValueOrDefault,
        getValueOrThrow,
      };
    }
    return null;
  }, [config, contexts, addValue, getValue, getValueOrDefault, getValueOrThrow]);

  if (result) {
    console.info('Inicializando ConfigProvider');
    return (
      <ConfigContext.Provider value={result}>
        <TogglesProvider onLoading={onLoading} onError={onError}>
          <ModeloApresentacaoProvider>{children}</ModeloApresentacaoProvider>
        </TogglesProvider>
      </ConfigContext.Provider>
    );
  }
  return <>{onLoading}</>;
};
